import { useMemo, JSX } from "react";

import {
  GeneralInformation,
  PlanRates,
  AccidentBenefits,
  AccidentCoverage,
  AccidentPlan,
} from "../../constants/aquafina";
import ContributionType from "../sections/contribution-type-section";
import { RangeIterator } from "../../utils";
import Rates from "../rates";
import CollapsableSection from "../sections/collapsable-section";
import { CoverageRow, NotProvidedRow } from "../table/rows";
import { CoverageCell } from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import ProposalGeneralInfo from "../sections/proposal-general-info";
import PolicyGeneralInfo from "../sections/policy-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: AccidentCoverage;
  displayV4Fields: boolean;
};

const Accident = ({ coverage, viewMode, displayV4Fields }: Props) => {
  const state = useMemo(() => {
    const benefitDetails: AccidentBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const contributionTypes = new Map<string, string[]>();
    const rates: PlanRates[] = [];
    const plans: AccidentPlan[] = [];
    let inconsistentRateData = false;
    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return {
      benefitDetails,
      generalInfo,
      contributionTypes,
      rates,
      plans,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}
      <AccidentBenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <AccidentAdditionalPlanFeatures
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
        viewMode={viewMode}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
};

type AccidentBenefitBaseProps = {
  benefits: AccidentBenefits[];
  displayV4Fields: boolean;
};

type AccidentAdditionalPlanFeaturesProps = AccidentBenefitBaseProps & {
  viewMode: ViewMode;
};

const AccidentBenefitDetails = ({
  benefits,
  displayV4Fields,
}: AccidentBenefitBaseProps): JSX.Element => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <AccidentBenefitDetailsData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const AccidentBenefitDetailsData = ({
  benefits,
  displayV4Fields,
}: AccidentBenefitBaseProps): JSX.Element => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageRow label="Coverage Type">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].coverageType} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Hospital Admission" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].hospitalAdmission} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="ICU Admission">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].icuAdmission} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Daily Hospital Confinement" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].dailyHospitalConfinement}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Daily ICU Confinement">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].icuConfinement} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Ambulance - Air" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].ambulanceAir} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Ambulance - Ground">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].ambulanceGround} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Emergency Treatment" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].emergencyTreatment}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Urgent Care Visit">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].urgentCare} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Initial Physician Office Visit" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].initialPhysicianOfficeVisit}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Dislocations">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].dislocations} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Fractures" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].fractures} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Laceration Maximum Benefit">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell value={benefits[index].lacerations} key={index} />
            ))}
          </CoverageRow>
          <CoverageRow label="Coma" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell value={benefits[index].coma} key={index} />
            ))}
          </CoverageRow>
          <CoverageRow label="Major Diagnostic Exam - X-Ray">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].majorDiagnosticExamXray}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Major Diagnostic Exam - MRI" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].majorDiagnosticExamMri}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
    </>
  );
};

const AccidentAdditionalPlanFeatures = ({
  benefits,
  displayV4Fields,
  viewMode,
}: AccidentAdditionalPlanFeaturesProps): JSX.Element => (
  <CollapsableSection
    numberOfColumns={benefits.length}
    title="Additional Plan Features"
  >
    <AccidentAdditionalPlanFeaturesData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
      viewMode={viewMode}
    />
  </CollapsableSection>
);

const AccidentAdditionalPlanFeaturesData = ({
  benefits,
  displayV4Fields,
  viewMode,
}: AccidentAdditionalPlanFeaturesProps): JSX.Element => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageRow
        label="Enhanced Benefit for Organized Sports-Related Accident"
        isWhite
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={
              benefits[index].enhancedBenefitOrganizedSportsRelatedAccident
            }
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="AD&D Benefit">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].isAdndIncluded} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Portability" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].portability} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Wellness Benefit">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].wellness} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Age Reduction Schedule" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].ageReductions} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields && viewMode !== VIEW_MODES.POLICY ? (
        <CoverageRow label="Annual Open Enrollment">
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].annualOpenEnrollment}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
    </>
  );
};

export default Accident;
